import {Component, ChangeDetectionStrategy} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {FeedbackService} from '../_services/feedback.service';
import {take} from 'rxjs';
import {SnackbarService} from '../_services/snackbar.service';
import {Community} from "../_types/communities";
import {LocalStorageService} from "../_services/local-storage.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AuthService} from "../_services/auth.service";
import {Feedback, FeedbackMessage, getFeedbackLastActivity, getFeedbackThread, hasStaffReply} from "../_types/feedback";
import {User} from "../_types/user";
import {CommunitiesService} from "../_services/communities.service";

@Component({
    selector: 'app-contact',
    templateUrl: './contact.component.html',
    styleUrl: './contact.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactComponent {
    loading: boolean = false;
    threadsLoading: boolean = false;
    feedbackThreads: Feedback[] = [];
    contactForm: FormGroup;
    community: Community | null = null
    currentUser: User | null = null;
    isLoggedIn: boolean = false;
    selectedCommunityId?: number;
    timer: number = 0;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly feedbackService: FeedbackService,
        private readonly communitiesService: CommunitiesService,
        private readonly snackbarService: SnackbarService,
        private readonly localStorageService: LocalStorageService,
        private readonly authService: AuthService,
    ) {
        this.contactForm = this.formBuilder.group({
            message: ['', Validators.required],
            isAnonymous: [false],
        });
        this.currentUser = this.authService.getUser();
        this.isLoggedIn = this.authService.isLoggedIn();
        this.selectedCommunityId = this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null)?.id;
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
                this.selectedCommunityId = community?.id ?? this.selectedCommunityId;
                if (this.isLoggedIn) {
                    this.loadThreads();
                }
            });
    }

    submit(): void {
        this.loading = true;
        this.feedbackService.sendMessage(this.contactForm.get('message')?.value, this.contactForm.get('isAnonymous')?.value, this.selectedCommunityId)
            .pipe(take(1))
            .subscribe({
                next: (): void => {
                    this.loading = false;
                    this.snackbarService.openSuccessSnackBar('Message sent successfully');
                    this.contactForm.get('message')?.reset('');
                    if (this.isLoggedIn) {
                        this.loadThreads();
                    }
                    this.timer = 30;
                    let timeleft: number = 30;
                    let that: this = this;
                    const downloadTimer = setInterval(function (): void {
                        if (timeleft <= 0) {
                            clearInterval(downloadTimer);
                        }
                        that.timer = timeleft;
                        timeleft -= 1;
                    }, 1000);
                },
                error: (err): void => {
                    this.loading = false;
                    this.snackbarService.openErrorSnackBar(err?.details ?? err);
                }
            })
    }

    loadThreads(): void {
        if (!this.isLoggedIn) {
            return;
        }

        this.threadsLoading = true;
        this.feedbackService.getAllMessages(undefined, 'All', this.selectedCommunityId)
            .pipe(take(1))
            .subscribe({
                next: (feedbacks: Feedback[]): void => {
                    this.feedbackThreads = feedbacks
                        .filter((feedback: Feedback): boolean => this.isOwnedByCurrentUser(feedback))
                        .sort((a: Feedback, b: Feedback): number =>
                            new Date(getFeedbackLastActivity(b)).getTime() - new Date(getFeedbackLastActivity(a)).getTime()
                        );
                    this.threadsLoading = false;
                },
                error: (err): void => {
                    this.threadsLoading = false;
                    if (err?.status !== 401 && err?.status !== 403) {
                        this.snackbarService.openErrorSnackBar(err?.details ?? err);
                    }
                }
            });
    }

    respond(feedback: Feedback): void {
        const body: string | undefined = feedback.replyDraft?.trim();
        if (!body || feedback.sendingReply) {
            return;
        }

        feedback.sendingReply = true;
        this.feedbackService.addThreadMessage(feedback.id, body)
            .pipe(take(1))
            .subscribe({
                next: (): void => {
                    feedback.sendingReply = false;
                    feedback.replyDraft = '';
                    feedback.responding = false;
                    this.loadThreads();
                    this.snackbarService.openSuccessSnackBar('Reply sent successfully');
                },
                error: (err): void => {
                    feedback.sendingReply = false;
                    this.snackbarService.openErrorSnackBar(err?.details ?? err);
                }
            });
    }

    thread(feedback: Feedback): FeedbackMessage[] {
        return getFeedbackThread(feedback);
    }

    lastActivity(feedback: Feedback): string | Date {
        return getFeedbackLastActivity(feedback);
    }

    hasStaffReply(feedback: Feedback): boolean {
        return hasStaffReply(feedback);
    }

    canSendReply(feedback: Feedback): boolean {
        return !!feedback.replyDraft?.trim() && !feedback.sendingReply;
    }

    toggleReply(feedback: Feedback): void {
        feedback.responding = !feedback.responding;
        if (!feedback.responding) {
            feedback.replyDraft = '';
        }
    }

    authorLabel(message: FeedbackMessage): string {
        if (message.is_from_staff) {
            return message.sender?.username?.trim() || 'Staff';
        }

        return message.sender?.username?.trim() || this.currentUser?.username?.trim() || 'You';
    }

    private isOwnedByCurrentUser(feedback: Feedback): boolean {
        if (!this.currentUser) {
            return false;
        }

        if (feedback.user_detail?.id === this.currentUser.id) {
            return true;
        }

        return getFeedbackThread(feedback).some((message: FeedbackMessage): boolean =>
            !message.is_from_staff && message.sender?.id === this.currentUser?.id
        );
    }
}
